import Quill from 'quill';
import React from 'react';
import ReactDOM from 'react-dom/client';
import ImageEditToolbar from './ImageEditToolbar';
import useSettingsStore from '../../stores/settingsStore';

const BaseImageFormat = Quill.import('formats/image');
const Module = Quill.import('core/module');

// Extend the default image format to support additional attributes
class ImageFormat extends BaseImageFormat {
  static formats(domNode) {
    return {
      ...super.formats(domNode),
      width: domNode.getAttribute('width'),
      height: domNode.getAttribute('height'),
      style: domNode.getAttribute('style'), // Preserve inline styles
      'data-align': domNode.getAttribute('data-align'),
      'data-float': domNode.getAttribute('data-float'),
      'data-margin-top': domNode.getAttribute('data-margin-top'),
      'data-margin-right': domNode.getAttribute('data-margin-right'),
      'data-margin-bottom': domNode.getAttribute('data-margin-bottom'),
      'data-margin-left': domNode.getAttribute('data-margin-left'),
      'alt': domNode.getAttribute('alt'),
      'title': domNode.getAttribute('title'),
      'data-link': domNode.getAttribute('data-link'),
      'data-border-width': domNode.getAttribute('data-border-width'),
      'data-border-color': domNode.getAttribute('data-border-color'),
      'data-border-radius': domNode.getAttribute('data-border-radius'),
      'data-shadow': domNode.getAttribute('data-shadow')
    };
  }

  format(name, value) {
    if (name === 'width' || name === 'height') {
      if (value) {
        const numValue = typeof value === 'string' ? parseInt(value) : value;
        this.domNode.setAttribute(name, numValue);
        this.domNode.style[name] = numValue + 'px';
      } else {
        this.domNode.removeAttribute(name);
        this.domNode.style[name] = '';
      }
    } else if (name === 'style') {
      // Handle style attribute
      if (value) {
        this.domNode.setAttribute('style', value);
      } else {
        this.domNode.removeAttribute('style');
      }
    } else if (name.startsWith('data-')) {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
      this.applyStyles();
    } else if (name === 'alt' || name === 'title') {
      if (value) {
        this.domNode.setAttribute(name, value);
      } else {
        this.domNode.removeAttribute(name);
      }
    } else {
      super.format(name, value);
    }
  }

  applyStyles() {
    const align = this.domNode.getAttribute('data-align');
    const float = this.domNode.getAttribute('data-float');
    const width = this.domNode.getAttribute('width');
    const height = this.domNode.getAttribute('height');
    const marginTop = this.domNode.getAttribute('data-margin-top') || '0';
    const marginRight = this.domNode.getAttribute('data-margin-right') || '0';
    const marginBottom = this.domNode.getAttribute('data-margin-bottom') || '0';
    const marginLeft = this.domNode.getAttribute('data-margin-left') || '0';
    const borderWidth = this.domNode.getAttribute('data-border-width') || '0';
    const borderColor = this.domNode.getAttribute('data-border-color') || '#ccc';
    const borderRadius = this.domNode.getAttribute('data-border-radius') || '0';
    const shadow = this.domNode.getAttribute('data-shadow') || 'none';
    
    let styles = [];
    
    // Always preserve dimensions
    if (width) {
      styles.push(`width: ${width}px`);
      styles.push('max-width: none'); // Override any default max-width
    }
    if (height) styles.push(`height: ${height}px`);
    
    // Apply margins
    styles.push(`margin: ${marginTop}px ${marginRight}px ${marginBottom}px ${marginLeft}px`);
    
    // Apply border
    if (borderWidth !== '0') {
      styles.push(`border: ${borderWidth}px solid ${borderColor}`);
      styles.push(`border-radius: ${borderRadius}px`);
    }
    
    // Apply shadow
    if (shadow !== 'none') {
      const shadowStyles = {
        'small': '0 1px 3px rgba(0,0,0,0.12)',
        'medium': '0 4px 6px rgba(0,0,0,0.15)',
        'large': '0 10px 20px rgba(0,0,0,0.19)'
      };
      styles.push(`box-shadow: ${shadowStyles[shadow] || shadow}`);
    }
    
    // Apply float or alignment
    if (float === 'left') {
      styles.push('float: left');
      styles.push('clear: left');
    } else if (float === 'right') {
      styles.push('float: right');
      styles.push('clear: right');
    } else {
      // Apply alignment only when not floating
      styles.push('display: block');
      if (align === 'center') {
        styles.push('margin-left: auto', 'margin-right: auto');
      } else if (align === 'right') {
        styles.push('margin-left: auto', 'margin-right: 0');
      } else {
        styles.push('margin-left: 0', 'margin-right: auto');
      }
    }
    
    this.domNode.style.cssText = styles.join('; ');
  }
}

// Register the extended format
Quill.register(ImageFormat, true);

class ImageResize extends Module {
  constructor(quill, options) {
    super(quill, options);
    this.quill = quill;
    this.options = options;
    
    // State
    this.selectedImage = null;
    this.overlay = null;
    this.toolbar = null;
    this.handles = {};
    this.isResizing = false;
    this.aspectRatio = 1;
    this.toolbarPosition = 'right';
    
    // Bind methods
    this.handleClick = this.handleClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.updateSelection = this.updateSelection.bind(this);
    
    // Initialize
    this.initializeListeners();
    this.createUI();
    
    // Initialize existing images immediately and after delay
    this.initializeExistingImages();
    setTimeout(() => {
      this.initializeExistingImages();
    }, 100);
  }
  
  initializeExistingImages() {
    // Find all images in the editor and ensure their styles are applied
    const images = this.quill.root.querySelectorAll('img');
    images.forEach(img => {
      const blot = Quill.find(img);
      if (blot && blot.constructor.name === 'ImageFormat') {
        // Force the ImageFormat to reapply its styles based on attributes
        blot.applyStyles();
      }
    });
  }

  initializeListeners() {
    // Image selection
    this.quill.root.addEventListener('click', this.handleClick);
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('click', this.handleClickOutside);
    
    // Update on content change
    this.quill.on('text-change', this.updateSelection);
    
    // When editor content is set (e.g., loading a PDF), reapply image styles
    this.quill.on('editor-change', (eventName, ...args) => {
      if (eventName === 'text-change') {
        // Content was changed, reapply image styles
        setTimeout(() => {
          this.initializeExistingImages();
        }, 50);
      }
    });
    
    // Also listen for DOM mutations to catch dynamic image insertions
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.tagName === 'IMG' || (node.querySelectorAll && node.querySelectorAll('img').length > 0)) {
            setTimeout(() => {
              this.initializeExistingImages();
            }, 50);
          }
        });
      });
    });
    
    observer.observe(this.quill.root, {
      childList: true,
      subtree: true
    });
    
    // Update toolbar position on scroll
    this.handleScroll = () => {
      if (this.selectedImage) {
        this.updateOverlay();
        this.updateToolbar();
      }
    };
    
    // Listen to both window and editor container scroll
    window.addEventListener('scroll', this.handleScroll, true);
    this.quill.container.addEventListener('scroll', this.handleScroll);
  }

  handleClick(e) {
    const img = e.target;
    
    if (img.tagName === 'IMG' && this.quill.root.contains(img)) {
      e.preventDefault();
      e.stopPropagation();
      this.selectImage(img);
    }
  }

  handleKeyDown(e) {
    if (e.key === 'Escape' && this.selectedImage) {
      this.deselectImage();
    }
  }

  handleClickOutside(e) {
    if (this.selectedImage && 
        !this.overlay?.contains(e.target) && 
        !this.toolbar?.contains(e.target) &&
        e.target !== this.selectedImage) {
      this.deselectImage();
    }
  }

  createUI() {
    // Create overlay for resize handles
    this.overlay = document.createElement('div');
    this.overlay.className = 'ql-image-resize-overlay';
    this.overlay.style.cssText = `
      position: absolute;
      border: 2px solid #0F3A61;
      pointer-events: none;
      z-index: 1000;
      display: none;
    `;
    
    // Create resize handles
    const positions = ['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'];
    positions.forEach(pos => {
      const handle = document.createElement('div');
      handle.className = `ql-resize-handle ql-resize-handle-${pos}`;
      handle.style.cssText = `
        position: absolute;
        width: 10px;
        height: 10px;
        background: #0F3A61;
        border: 2px solid white;
        border-radius: 50%;
        pointer-events: auto;
        cursor: ${this.getCursor(pos)};
      `;
      
      // Position handles
      if (pos.includes('n')) handle.style.top = '-7px';
      if (pos.includes('s')) handle.style.bottom = '-7px';
      if (pos.includes('e')) handle.style.right = '-7px';
      if (pos.includes('w')) handle.style.left = '-7px';
      
      if (pos === 'n' || pos === 's') {
        handle.style.left = '50%';
        handle.style.transform = 'translateX(-50%)';
      }
      if (pos === 'e' || pos === 'w') {
        handle.style.top = '50%';
        handle.style.transform = 'translateY(-50%)';
      }
      
      // Add resize event handlers
      handle.addEventListener('mousedown', (e) => this.startResize(e, pos));
      
      this.handles[pos] = handle;
      this.overlay.appendChild(handle);
    });
    
    // Add overlay to editor container
    this.quill.container.style.position = 'relative';
    this.quill.container.appendChild(this.overlay);
    
    // Create inline toolbar
    this.createToolbar();
  }

  createToolbar() {
    this.toolbar = document.createElement('div');
    this.toolbar.className = 'ql-image-toolbar';
    this.toolbar.style.cssText = `
      position: fixed;
      display: none;
      z-index: 1001;
      opacity: 0;
      transition: opacity 0.2s ease-in-out;
    `;
    
    // Create toolbar content using React
    const root = ReactDOM.createRoot(this.toolbar);
    this.toolbarRoot = root;
    
    // Add toolbar to body (to avoid Quill container issues)
    document.body.appendChild(this.toolbar);
  }

  getCursor(position) {
    const cursors = {
      'nw': 'nw-resize',
      'ne': 'ne-resize',
      'sw': 'sw-resize',
      'se': 'se-resize',
      'n': 'n-resize',
      's': 's-resize',
      'e': 'e-resize',
      'w': 'w-resize'
    };
    return cursors[position] || 'move';
  }

  selectImage(img) {
    this.selectedImage = img;
    this.aspectRatio = img.naturalWidth / img.naturalHeight;
    
    // Show overlay immediately
    this.updateOverlay();
    this.overlay.style.display = 'block';
    
    // Reset toolbar for clean state
    this.toolbar.style.opacity = '0';
    this.toolbar.style.display = 'block';
    this.toolbarPosition = null;
    
    // Update toolbar content first
    this.renderToolbar();
    
    // Then calculate and apply position
    requestAnimationFrame(() => {
      this.updateToolbar();
      
      // Fade in after position is set
      requestAnimationFrame(() => {
        this.toolbar.style.opacity = '1';
      });
    });
  }

  deselectImage() {
    this.selectedImage = null;
    this.overlay.style.display = 'none';
    
    // Fade out toolbar
    this.toolbar.style.opacity = '0';
    setTimeout(() => {
      this.toolbar.style.display = 'none';
    }, 200);
    
    this.toolbarPosition = null; // Reset position for next selection
  }

  updateOverlay() {
    if (!this.selectedImage) return;
    
    const rect = this.selectedImage.getBoundingClientRect();
    const containerRect = this.quill.container.getBoundingClientRect();
    
    this.overlay.style.left = (rect.left - containerRect.left - 2) + 'px';
    this.overlay.style.top = (rect.top - containerRect.top - 2) + 'px';
    this.overlay.style.width = rect.width + 'px';
    this.overlay.style.height = rect.height + 'px';
  }

  updateToolbar() {
    if (!this.selectedImage) return;
    
    const rect = this.selectedImage.getBoundingClientRect();
    const editorRect = this.quill.container.getBoundingClientRect();
    const toolbarWidth = 320; // Max width of toolbar
    const toolbarHeight = 400; // Approximate max height
    const margin = 10;
    
    // Get image float style
    const imageFloat = this.selectedImage.getAttribute('data-float') || null;
    
    // Use previously determined position if available (for stability)
    let position = this.toolbarPosition || 'right';
    
    // Only recalculate position on first selection or if forced
    if (!this.toolbarPosition) {
      if (imageFloat === 'right') {
        // If image is floated right, put toolbar on left
        position = 'left';
      } else if (imageFloat === 'left') {
        // If image is floated left, put toolbar on right
        position = 'right';
      } else {
        // No float - check available space
        const spaceLeft = rect.left;
        const spaceRight = window.innerWidth - rect.right;
        
        // Choose side with more space
        if (spaceLeft > spaceRight && spaceLeft > toolbarWidth + margin) {
          position = 'left';
        } else if (spaceRight > toolbarWidth + margin) {
          position = 'right';
        } else {
          // Not enough space on sides, fall back to top/bottom
          position = 'top';
        }
      }
      this.toolbarPosition = position;
    }
    
    // Apply positioning based on chosen position
    if (position === 'left') {
      this.toolbar.style.left = (rect.left - toolbarWidth - margin) + 'px';
      this.toolbar.style.top = rect.top + 'px';
      this.toolbar.style.transform = 'none';
      
      // Adjust if would go off-screen
      if (rect.left - toolbarWidth - margin < 0) {
        this.toolbar.style.left = '10px';
      }
    } else if (position === 'right') {
      this.toolbar.style.left = (rect.right + margin) + 'px';
      this.toolbar.style.top = rect.top + 'px';
      this.toolbar.style.transform = 'none';
      
      // Adjust if would go off-screen
      if (rect.right + toolbarWidth + margin > window.innerWidth) {
        this.toolbar.style.left = (window.innerWidth - toolbarWidth - 10) + 'px';
      }
    } else {
      // Top/bottom positioning (fallback)
      this.toolbar.style.left = rect.left + 'px';
      this.toolbar.style.top = (rect.top - margin) + 'px';
      this.toolbar.style.transform = 'translateY(-100%)';
      
      // If toolbar would be off-screen at top, position below
      if (rect.top - toolbarHeight < 10) {
        this.toolbar.style.top = (rect.bottom + margin) + 'px';
        this.toolbar.style.transform = 'translateY(0)';
      }
    }
    
    // Ensure toolbar stays within viewport vertically
    const toolbarRect = this.toolbar.getBoundingClientRect();
    if (toolbarRect.bottom > window.innerHeight - 10) {
      this.toolbar.style.top = (window.innerHeight - toolbarRect.height - 10) + 'px';
    }
    if (toolbarRect.top < 10) {
      this.toolbar.style.top = '10px';
    }
    
    // Save position and re-render if changed
    const newPosition = position;
    if (this.toolbarPosition !== newPosition) {
      this.toolbarPosition = newPosition;
      this.renderToolbar();
    }
  }

  renderToolbar() {
    if (!this.toolbarRoot || !this.selectedImage) return;
    
    const handleUpdate = (updates) => {
      const blot = Quill.find(this.selectedImage);
      if (blot) {
        Object.entries(updates).forEach(([key, value]) => {
          blot.format(key, value);
        });
        this.quill.update('user');
        this.updateOverlay();
        this.updateToolbar();
      }
    };
    
    // Get theme from settings store (same way as ThemeToggle component)
    const getCurrentTheme = () => {
      try {
        const settings = useSettingsStore.getState().settings;
        return settings.theme || 'light';
      } catch (error) {
        console.warn('Failed to get theme from settings store:', error);
        return 'light';
      }
    };
    
    // Use saved toolbar position
    const getToolbarPosition = () => this.toolbarPosition || 'right';
    
    this.toolbarRoot.render(
      React.createElement(ImageEditToolbar, {
        image: this.selectedImage,
        onUpdate: handleUpdate,
        onClose: () => this.deselectImage(),
        aspectRatio: this.aspectRatio,
        themeMode: getCurrentTheme(),
        position: getToolbarPosition()
      })
    );
  }

  startResize(e, handle) {
    e.preventDefault();
    this.isResizing = true;
    
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = this.selectedImage.width || this.selectedImage.naturalWidth;
    const startHeight = this.selectedImage.height || this.selectedImage.naturalHeight;
    
    const handleMouseMove = (e) => {
      if (!this.isResizing) return;
      
      const deltaX = e.clientX - startX;
      const deltaY = e.clientY - startY;
      
      let newWidth = startWidth;
      let newHeight = startHeight;
      
      // Calculate new dimensions based on handle
      if (handle.includes('e')) newWidth = startWidth + deltaX;
      if (handle.includes('w')) newWidth = startWidth - deltaX;
      if (handle.includes('s')) newHeight = startHeight + deltaY;
      if (handle.includes('n')) newHeight = startHeight - deltaY;
      
      // Maintain aspect ratio for corner handles
      if (handle.length === 2) {
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          newHeight = newWidth / this.aspectRatio;
        } else {
          newWidth = newHeight * this.aspectRatio;
        }
      }
      
      // Apply minimum size
      newWidth = Math.max(50, newWidth);
      newHeight = Math.max(50, newHeight);
      
      // Update image
      const blot = Quill.find(this.selectedImage);
      if (blot) {
        blot.format('width', Math.round(newWidth));
        blot.format('height', Math.round(newHeight));
        this.quill.update('user');
        this.updateOverlay();
        this.updateToolbar();
        this.renderToolbar();
      }
    };
    
    const handleMouseUp = () => {
      this.isResizing = false;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }

  updateSelection() {
    // Update overlay position if image is selected
    if (this.selectedImage && this.quill.root.contains(this.selectedImage)) {
      this.updateOverlay();
      // Don't reset position during updates, just adjust coordinates
      const currentPosition = this.toolbarPosition;
      this.updateToolbar();
      this.toolbarPosition = currentPosition; // Preserve position choice
      // Re-render toolbar to reflect any attribute changes
      this.renderToolbar();
    } else if (this.selectedImage) {
      this.deselectImage();
    }
  }

  destroy() {
    // Remove event listeners
    this.quill.root.removeEventListener('click', this.handleClick);
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('click', this.handleClickOutside);
    window.removeEventListener('scroll', this.handleScroll, true);
    this.quill.container.removeEventListener('scroll', this.handleScroll);
    
    // Remove UI elements
    if (this.overlay && this.overlay.parentNode) {
      this.overlay.parentNode.removeChild(this.overlay);
    }
    if (this.toolbar && this.toolbar.parentNode) {
      this.toolbar.parentNode.removeChild(this.toolbar);
    }
    
    // Unmount React
    if (this.toolbarRoot) {
      this.toolbarRoot.unmount();
    }
  }
}

export default ImageResize;